var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
/**
 * API service for the authority validation framework (TASK-159 / AV-01).
 *
 * Three endpoints:
 *  - `validateRecord` — ad-hoc preflight check, returns ValidationResult, no DB writes.
 *  - `revalidate`     — kick off async bulk job (scope ALL / TYPE / RECORD); 202 ack.
 *  - `getJobStatus`   — poll a previously-submitted bulk job by id.
 *
 * All three follow the project's `useOkapiKy()` pattern (see MEMORY: never put
 * `ky` into useEffect/useMemo deps — components capture it by closure or use
 * react-query). The 422 defense-in-depth response on save/publish endpoints
 * carries the same {@link ValidationResult} payload — see {@link parseValidationFailedError}.
 */
import { getHeaders } from './apiUtils';
import { API_PATHS, CZNA_VALIDATION_REVALIDATE_JOB, } from '../types/apiPaths';
/**
 * Probe the currently active revalidation job (QUEUED or RUNNING). Resolves
 * to `null` when the backend returned 204 (nothing is running). Used by the
 * list view to render the progress banner after a page reload and to
 * disable the "Revalidate" menu items while a job is in flight.
 *
 * Anything other than 204 + 200 propagates as an HTTPError so the caller can
 * surface a transient backend issue.
 */
export var getCurrentJob = function (ky) { return __awaiter(void 0, void 0, void 0, function () {
    var response;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0: return [4 /*yield*/, ky.get(API_PATHS.CZNA_VALIDATION_REVALIDATE_CURRENT, {
                    headers: getHeaders(),
                })];
            case 1:
                response = _a.sent();
                if (response.status === 204)
                    return [2 /*return*/, null];
                return [2 /*return*/, response.json()];
        }
    });
}); };
/**
 * Ad-hoc validation. Used by the "Zkontrolovat znovu" button in the record
 * detail panel and by ProposalForm preflight on submit. Does NOT mutate any
 * record state — clients still need to call save afterwards.
 */
export var validateRecord = function (ky, req) { return __awaiter(void 0, void 0, void 0, function () {
    return __generator(this, function (_a) {
        return [2 /*return*/, ky.post(API_PATHS.CZNA_VALIDATION_VALIDATE_RECORD, {
                headers: getHeaders(),
                json: req,
            }).json()];
    });
}); };
/**
 * CZNA-328 — corpus-sample mode of the MARC Checker. Runs the stored rule set
 * (all rules, or one rule when `req.ruleCode` is set) against a bounded sample
 * of live records of `req.recordType`. The same `/validate-record` route is
 * reused; the backend switches to the sample path when `sampleSize` is present,
 * returning a {@link ValidationSampleResult}. `record` is omitted by the caller.
 */
export var validateRecordSample = function (ky, req) { return __awaiter(void 0, void 0, void 0, function () {
    return __generator(this, function (_a) {
        return [2 /*return*/, ky.post(API_PATHS.CZNA_VALIDATION_VALIDATE_RECORD, {
                headers: getHeaders(),
                json: req,
            }).json()];
    });
}); };
/**
 * CZNA-684 — ask the backend which control fields it will supply for this record.
 * Validates nothing; the same `/validate-record` route is reused (the backend switches
 * to the derive path on `deriveControlFields`), so this needs no new Kong route, no new
 * permission and no capability grant — the caller already holds this one.
 *
 * Used by the proposal form's MARC preview. Before CZNA-684 the preview omitted every
 * field the system fills in, so it showed an incomplete record and the save produced a
 * different one.
 */
export var deriveControlFields = function (ky, req) { return __awaiter(void 0, void 0, void 0, function () {
    return __generator(this, function (_a) {
        return [2 /*return*/, ky.post(API_PATHS.CZNA_VALIDATION_VALIDATE_RECORD, {
                headers: getHeaders(),
                json: __assign(__assign({}, req), { deriveControlFields: true }),
            }).json()];
    });
}); };
/**
 * CZNA-1418 — compose the heading the save would store for these form rows. Composes,
 * validates nothing; a mode of `/validate-record` for the same reason as
 * {@link deriveControlFields} (same caller, same permission, no new Kong route).
 *
 * Used by the proposal form's heading preview. Before CZNA-1418 the preview composed the
 * heading itself, in a TypeScript copy of the backend composer that kept one value per
 * subfield code — so a work with two `$p` showed one and stored both.
 */
export var composeHeading = function (ky, req, signal) { return __awaiter(void 0, void 0, void 0, function () {
    return __generator(this, function (_a) {
        return [2 /*return*/, ky.post(API_PATHS.CZNA_VALIDATION_VALIDATE_RECORD, {
                headers: getHeaders(),
                json: __assign(__assign({}, req), { operation: 'CREATE_DRAFT', composeHeading: true }),
                signal: signal,
            }).json()];
    });
}); };
/**
 * Kick off an async bulk revalidate job. Backend enforces single-job-at-a-time
 * via an advisory lock; a 409 carries the running job id in the body. Callers
 * should treat HTTPError separately so the warning UI can render the in-flight
 * job link.
 */
export var revalidate = function (ky, req) { return __awaiter(void 0, void 0, void 0, function () {
    return __generator(this, function (_a) {
        return [2 /*return*/, ky.post(API_PATHS.CZNA_VALIDATION_REVALIDATE, {
                headers: getHeaders(),
                json: req,
            }).json()];
    });
}); };
/**
 * Poll status of a previously-submitted bulk job. Polling cadence is the
 * responsibility of the caller (see {@link useRevalidateJob}) — this is a
 * single GET. 404 surfaces as a ky HTTPError; callers should treat it as
 * "job not found / expired".
 */
export var getJobStatus = function (ky, jobId) { return __awaiter(void 0, void 0, void 0, function () {
    return __generator(this, function (_a) {
        return [2 /*return*/, ky.get(CZNA_VALIDATION_REVALIDATE_JOB(jobId), {
                headers: getHeaders(),
            }).json()];
    });
}); };
/**
 * Try to parse a 422 ValidationFailed response from a thrown ky error. Returns
 * the parsed {@link ValidationFailedResponse} when the body matches the
 * expected envelope (defense-in-depth on save/publish), otherwise null. Used
 * by save-mutation error handlers in ProposalForm so the UI can surface
 * server-side BLOCKING errors with the same banner + inline-error treatment
 * as the client-side preflight.
 */
export var parseValidationFailedError = function (error) { return __awaiter(void 0, void 0, void 0, function () {
    var maybeResp, body, _a;
    return __generator(this, function (_b) {
        switch (_b.label) {
            case 0:
                if (!error || typeof error !== 'object')
                    return [2 /*return*/, null];
                maybeResp = error.response;
                if (!maybeResp || maybeResp.status !== 422 || typeof maybeResp.json !== 'function') {
                    return [2 /*return*/, null];
                }
                _b.label = 1;
            case 1:
                _b.trys.push([1, 3, , 4]);
                return [4 /*yield*/, maybeResp.json()];
            case 2:
                body = _b.sent();
                if (body
                    && typeof body === 'object'
                    && 'validationResult' in body) {
                    return [2 /*return*/, body];
                }
                return [3 /*break*/, 4];
            case 3:
                _a = _b.sent();
                return [3 /*break*/, 4];
            case 4: return [2 /*return*/, null];
        }
    });
}); };
